import { Search, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { displayCategoryName } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import {
  Button,
  Checkbox,
  Input,
  Label,
  MultiSelect,
  type MultiSelectOption,
  SearchableMultiSelect,
} from "~/components/ui";

import { useChangeRequestModelContext } from "~/features/changeRequests/hooks";
import {
  CHANGE_REQUEST_MODEL_TYPES,
  CHANGE_REQUEST_STATUSES,
  type ChangeRequestListFilters,
} from "~/features/changeRequests/types";

interface ChangeRequestsSearchFormProps {
  filters: ChangeRequestListFilters;
  onApply: (next: ChangeRequestListFilters) => void;
}

export function ChangeRequestsSearchForm({ filters, onApply }: ChangeRequestsSearchFormProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { dataImportProjects, categories } = useChangeRequestModelContext();

  const [search, setSearch] = useState(filters.search ?? "");
  const [statuses, setStatuses] = useState<string[]>(filters.statuses);
  const [modelTypes, setModelTypes] = useState<string[]>(filters.modelTypes);
  const [hasReviewComments, setHasReviewComments] = useState(filters.hasReviewComments);
  const [categoryIds, setCategoryIds] = useState<string[]>(filters.categoryIds);
  const [dataImportProjectIds, setDataImportProjectIds] = useState<string[]>(filters.dataImportProjectIds);

  // Re-seed the draft whenever committed URL filters change from outside the form
  // (back/forward, shared link). `filters` is a stable useMemo reference in the container.
  const prevFiltersRef = useRef(filters);
  if (prevFiltersRef.current !== filters) {
    prevFiltersRef.current = filters;
    setSearch(filters.search ?? "");
    setStatuses(filters.statuses);
    setModelTypes(filters.modelTypes);
    setHasReviewComments(filters.hasReviewComments);
    setCategoryIds(filters.categoryIds);
    setDataImportProjectIds(filters.dataImportProjectIds);
  }

  const statusOptions = useMemo(
    () => CHANGE_REQUEST_STATUSES.map((s) => ({ value: s, label: t(`feature.changeRequests.status.${s}`) })),
    [t],
  );
  const modelTypeOptions = useMemo(
    () => CHANGE_REQUEST_MODEL_TYPES.map((m) => ({ value: m, label: t(`feature.changeRequests.modelType.${m}`) })),
    [t],
  );
  const categoryOptions = useMemo<MultiSelectOption[]>(
    () =>
      categories.map((c) => ({
        id: c.id,
        label: displayCategoryName({ name: c.name, i18nLabel: c.i18nLabel as Record<string, unknown> }, locale),
      })),
    [categories, locale],
  );
  const dataImportProjectOptions = useMemo<MultiSelectOption[]>(
    () => dataImportProjects.map((d) => ({ id: d.id, label: d.name })),
    [dataImportProjects],
  );

  const submit = () => {
    onApply({
      search: search.trim() || undefined,
      statuses,
      modelTypes,
      hasReviewComments,
      categoryIds,
      dataImportProjectIds,
    });
  };

  const reset = () => {
    setSearch("");
    setStatuses([]);
    setModelTypes([]);
    setHasReviewComments(false);
    setCategoryIds([]);
    setDataImportProjectIds([]);
    onApply({ statuses: [], modelTypes: [], hasReviewComments: false, categoryIds: [], dataImportProjectIds: [] });
  };

  return (
    <form
      className="flex flex-col gap-5 text-[13px]"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cr-search" className="text-12-bold capitalize text-fg-2">
          {t("ui.search")}
        </Label>
        <Input id="cr-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("ui.search")} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.changeRequest.status")}</Label>
        <MultiSelect
          label={t("model.changeRequest.status")}
          options={statusOptions}
          selected={statuses}
          onChange={setStatuses}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.changeRequest.changeType")}</Label>
        <MultiSelect
          label={t("model.changeRequest.changeType")}
          options={modelTypeOptions}
          selected={modelTypes}
          onChange={setModelTypes}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.category.modelNames")}</Label>
        <SearchableMultiSelect
          placeholder={t("model.category.modelNames")}
          searchPlaceholder={t("ui.search")}
          options={categoryOptions}
          selectedIds={categoryIds}
          onChange={setCategoryIds}
          emptyText={t("feature.common.noData")}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.dataImportProject.modelNames")}</Label>
        <SearchableMultiSelect
          placeholder={t("model.dataImportProject.modelNames")}
          searchPlaceholder={t("ui.search")}
          options={dataImportProjectOptions}
          selectedIds={dataImportProjectIds}
          onChange={setDataImportProjectIds}
          emptyText={t("feature.common.noData")}
        />
      </div>

      <label htmlFor="cr-has-comments" className="flex items-center gap-2 text-fg-1">
        <Checkbox
          id="cr-has-comments"
          checked={hasReviewComments}
          onCheckedChange={(checked) => setHasReviewComments(checked === true)}
        />
        <span>{t("feature.changeRequests.reviewComments")}</span>
      </label>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          <Search />
          <span>{t("ui.search")}</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={reset}>
          <X />
          <span>{t("ui.reset")}</span>
        </Button>
      </div>
    </form>
  );
}
